import { useCallback, RefObject, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import { CellValueChangedEvent } from "ag-grid-community";
import { getItemPopupList } from "src/apis/itemApi";
import { SYS4030_R01Dto } from "../Dto_SYS4030_R01";
import { ALLOWED_ITEM_ACCOUNTS } from "src/components/common/common";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { getEmployeeList } from "src/apis/employeeApi";
import { focusToCell } from "src/utils/grid/GridHandlerContent";
import { useDetailTableHandlers } from "src/hooks/useDetailTableHandlers";
import { DetailTableFieldConfig } from "src/types/detail-table";
import { CodeSearchConfig as CodeSearchConfigType } from "src/types/code-search";
import { getCustomerList, searchCustomer } from "src/apis/customerApi";

interface UseSYS4030_R01CellChangeProps {
	SYS4030_R01List: SYS4030_R01Dto[];
	updateSYS4030_R01List: (index: number, data: SYS4030_R01Dto) => void;
	originalSYS4030_R01List: SYS4030_R01Dto[];
	settingCodeSearchModalProps: (
		category: string,
		search: string,
		isInfo: boolean,
		handleFoucsId: string,
		originalData: SYS4030_R01Dto,
		valueIndex: number,
		extraFilter?: { itemAccount?: string }
	) => void;
	gridRef: RefObject<AgGridReact<SYS4030_R01Dto> | null>;
}

export function useSYS4030_R01CellChange({
	SYS4030_R01List,
	updateSYS4030_R01List,
	originalSYS4030_R01List,
	settingCodeSearchModalProps,
	gridRef
}: UseSYS4030_R01CellChangeProps) {

	const { customCdList } = useCommonCodeStore();

	// fieldConfig 설정 (useDetailTableHandlers용)
	const fieldConfig: DetailTableFieldConfig = useMemo(() => ({
		qtyField: '',
		priceField: '', // SYS4030_R01에는 없지만 필수 필드
		unitField: '',
		codeSearchFields: {
			customCd: {
				codeField: 'customCd',
				category: '거래처',
				responseMapping: {
					customCd: 'customCd',
					customNm: 'customNm'
				},
				clearFields: {
					customCd: '',
					customNm: ''
				},
				nextFocusCell: 'remark', // 거래처 검색 성공 시 비고로 포커스 이동
				searchFunction: async (code: string, onFound: (item: any) => void) => {
					try {
						const response = await searchCustomer(code);
						const found = response.data.data.find((customer: { customCd: string }) => customer.customCd === code);
						if (found) {
							onFound(found);
							return found;
						}
						return null;
					} catch (error) {
						console.error('거래처 검색 중 오류:', error);
						return null;
					}
				}
			}
		}
	}), []);

	// codeSearchConfig 설정 (useDetailTableHandlers용)
	const codeSearchConfig: CodeSearchConfigType = useMemo(() => ({
		formName: 'SYS4030_R01',
		useLegacyFocus: false // 포커스 이동은 fieldConfig의 nextFocusCell에서 처리
	}), []);

	// calculateAmount 함수 (SYS4030_R01에는 금액 계산이 없으므로 단순 반환)
	const calculateAmount = useCallback((data: SYS4030_R01Dto): SYS4030_R01Dto => {
		return data; // SYS4030_R01에는 금액 계산이 없음
	}, []);

	// useDetailTableHandlers 사용 (공통 코드 검색 로직)
	const commonHandlers = useDetailTableHandlers<SYS4030_R01Dto>({
		detailList: SYS4030_R01List,
		updateDetailList: updateSYS4030_R01List,
		originalDetailList: originalSYS4030_R01List,
		setCodeSearchModalProps: (props: any) => {
			// SYS4030_R01의 settingCodeSearchModalProps로 변환
			settingCodeSearchModalProps(
				props.category,
				props.search,
				props.isInfo || false,
				props.handleFoucsId || props.handleFocusId,
				props.originalData || props.rowData,
				props.valueIndex || props.rowIndex,
				props.extraFilter
			);
		},
		gridRef: gridRef as any,
		fieldConfig,
		codeSearchConfig,
		calculateAmount
	});

	// SYS4030_R01 특화 셀 변경 핸들러 (하이브리드 접근)
	const handleCellChange = useCallback(
		async (event: CellValueChangedEvent<SYS4030_R01Dto>) => {
			const { data, colDef, newValue, oldValue } = event;
			if (!data || !colDef.field)	return;
			const field = colDef.field as keyof SYS4030_R01Dto;

			let isOrderDtModified = true;

			// 공통 코드 검색 로직은 useDetailTableHandlers 사용
			const codeFieldConfig = fieldConfig.codeSearchFields?.[field as string];
			if (codeFieldConfig && newValue !== oldValue) {
				await commonHandlers.handleCellChange(event);
				return;
			}

			// 기타 필드 변경 처리
			updateSYS4030_R01List(data.valueIndex!, {
				...data,
				[field]: newValue,
				isModified: isOrderDtModified ? true : false
			});
		}, [commonHandlers, fieldConfig, updateSYS4030_R01List, customCdList]);

	const handleCustomCdSearch = useCallback(
		async (customCd: string, rowData: SYS4030_R01Dto, oldValue?: string) => {
			const codeFieldConfig = fieldConfig.codeSearchFields?.orgAdmin;
			if (!codeFieldConfig) return;

			// useDetailTableHandlers의 handleCellChange를 시뮬레이션
			const mockEvent = {
				data: rowData,
				colDef: { field: 'customCd' },
				newValue: customCd,
				oldValue: oldValue
			} as CellValueChangedEvent<SYS4030_R01Dto>;

			await commonHandlers.handleCellChange(mockEvent);
		}, [commonHandlers, fieldConfig]);

	return {
		handleCellChange,
		handleCustomCdSearch
	};
}
